/**
 * Which questionnaire figures belong in a report, and which of them are switched on.
 * Figure names are score labels (Søvn, Sundhedsprofil), never a patient or CPR.
 */

import type { ChartFigure } from '@/lib/patientReports/chartFigure';
import type { ScoringConfig } from '@/lib/formsScoring';
import { resolveAssignmentScores } from '@/lib/formsScoring';
import type { PatientReportCatalog } from '@/lib/patientReports/model';
import { formatClinicDateOnly } from '@/lib/questionnaireDateFormat';
import {
  questionnaireSeverityBands,
  scoreNumericValue,
  type ScoreDisplay,
} from '@/lib/questionnaireScoreDisplay';
import { getSeverityLabel } from '@/lib/severityUtils';

type FigureQuestion = {
  id: string;
  type: string;
  category_id?: string;
  options?: { value?: string; score?: number }[];
  scaleMax?: number;
  scoring?: { method?: string; weight?: number; max_score?: number };
};

export type ChartChoice = {
  id: string;
  label: string;
};

export type ChartSelection = {
  parts: { charts: boolean };
  chartIds: string[] | null;
};

const SCORE_KEYS = ['total_score', 'totalScore', 'total', 'score', 'value', 'physical_sum', 'pcs'];

function danish(language: string): boolean {
  return !language.toLowerCase().startsWith('en');
}

function finite(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const next = Number(value.trim().replace(',', '.'));
    if (Number.isFinite(next)) return next;
  }
  return null;
}

/** Id stored on the section. Letters, digits and hyphen only, so the sanitizer keeps it. */
export function chartIdFromLabel(label: string, used: Set<string>): string {
  const folded = label
    .trim()
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
  const base = `bar-${folded || 'score'}`.slice(0, 80);
  let id = base;
  let n = 2;
  while (used.has(id)) {
    const suffix = `-${n}`;
    id = `${base.slice(0, Math.max(4, 80 - suffix.length))}${suffix}`;
    n += 1;
  }
  used.add(id);
  return id;
}

function bar(
  id: string,
  label: string,
  value: number,
  max: number,
): ChartFigure {
  return { id, kind: 'bar', label, value, max };
}

function severityName(severity: string, language: string): string {
  const english = !danish(language);
  const t = (_key: string, fallback: string) => {
    if (!english) return fallback;
    if (severity === 'mild') return 'Mild';
    if (severity === 'moderate') return 'Moderate';
    if (severity === 'moderately_severe') return 'Moderately severe';
    if (severity === 'severe') return 'Severe';
    if (severity === 'minimal') return 'Minimal';
    return fallback;
  };
  return getSeverityLabel(severity as 'minimal', t);
}

/**
 * Same domain list as the questionnaire Sundhedsprofil.
 * Saved domain scores win. A local recalculation is only used when none are saved,
 * and it is the same function the questionnaire view uses. Scale is 0–100.
 */
export function questionnaireCharts(input: {
  questions: readonly FigureQuestion[];
  responses: Record<string, unknown> | null | undefined;
  scoreDetails: unknown;
  scoringConfig: ScoringConfig | null | undefined;
  templateName: string;
  language: string;
  shown: ScoreDisplay;
}): ChartFigure[] {
  const resolved = resolveAssignmentScores(
    input.questions.map((question) => ({
      id: question.id,
      text: '',
      type: question.type,
      category_id: question.category_id,
      options: question.options?.flatMap((option) =>
        option.value != null
          ? [{ value: option.value, label: option.value, score: option.score }]
          : [],
      ),
      scoring: question.scoring,
    })),
    input.responses ?? {},
    input.scoringConfig,
    input.scoreDetails,
  );
  const domains = (resolved?.domainScores ?? []).flatMap((domain) => {
    const label = typeof domain.domain === 'string' ? domain.domain.trim() : '';
    const value = finite(domain.score);
    if (!label || value == null) return [];
    return [{ label, value }];
  });
  const used = new Set<string>(['radar', 'scale', 'history', 'bar-score', 'bar-physical', 'bar-mental']);
  const charts: ChartFigure[] = [];
  const da = danish(input.language);

  const numeric = scoreNumericValue(input.shown);
  const bands = questionnaireSeverityBands(input.templateName);
  if (bands && numeric != null) {
    const last = bands[bands.length - 1];
    charts.push({
      id: 'scale',
      kind: 'scale',
      label: da ? 'Scoreskala' : 'Score scale',
      value: numeric,
      max: last?.max ?? Math.max(numeric, 1),
      points: bands.map((band) => ({
        label: severityName(band.severity, input.language),
        value: band.min,
        max: band.max,
      })),
    });
  }

  const bars = domains.map((domain) =>
    bar(chartIdFromLabel(domain.label, used), domain.label, domain.value, 100),
  );

  if (bars.length >= 3) {
    const average = bars.reduce((sum, item) => sum + item.value, 0) / bars.length;
    const total = finite(resolved?.totalScore);
    charts.push({
      id: 'radar',
      kind: 'radar',
      label: da ? 'Sundhedsprofil' : 'Health profile',
      value: total ?? average,
      max: 100,
      points: bars.map((item) => ({ label: item.label, value: item.value, max: 100 })),
    });
  }

  if (bars.length > 0) {
    charts.push(...bars);
    return charts;
  }

  if (input.shown.kind === 'total') {
    charts.push(
      bar(
        'bar-score',
        'Score',
        input.shown.value,
        input.shown.max ?? Math.max(input.shown.value, 1),
      ),
    );
  } else if (input.shown.kind === 'physical_mental') {
    charts.push(
      bar('bar-physical', da ? 'Fysisk' : 'Physical', input.shown.physical, 100),
      bar('bar-mental', da ? 'Mental' : 'Mental', input.shown.mental, 100),
    );
  }
  return charts;
}

/** A single number from the catalog's score line. Not a patient name. */
export function primaryScoreFromText(scoreText: string | null | undefined): number | null {
  if (!scoreText) return null;
  const text = scoreText.trim();
  if (/^-?\d+(?:[.,]\d+)?$/.test(text)) return Number(text.replace(',', '.'));
  const slash = text.match(/^(-?\d+(?:[.,]\d+)?)\s*\/\s*-?\d+/);
  if (slash?.[1]) return Number(slash[1].replace(',', '.'));
  for (const key of SCORE_KEYS) {
    const match = text.match(new RegExp(`(?:^|\\n)${key}:\\s*(-?\\d+(?:[.,]\\d+)?)`, 'i'));
    if (match?.[1]) return Number(match[1].replace(',', '.'));
  }
  return null;
}

export function historyChartForTemplate(input: {
  catalog: PatientReportCatalog;
  formsTemplateId: string | null;
  templateName: string;
  /** Filled when the open response's catalog line has no number yet. */
  current?: { id: string; scoreText: string | null } | null;
  language: string;
}): ChartFigure | null {
  const templateId = input.formsTemplateId;
  const rows = input.catalog.questionnaires.filter((item) => {
    if (item.status && item.status !== 'completed') return false;
    if (templateId) return item.formsTemplateId === templateId;
    return Boolean(input.templateName) && item.name === input.templateName;
  });
  const points = rows
    .map((item) => {
      const fromCatalog = primaryScoreFromText(item.scoreText);
      const value =
        fromCatalog ??
        (input.current && item.id === input.current.id
          ? primaryScoreFromText(input.current.scoreText)
          : null);
      return { at: item.completedAt, value };
    })
    .filter((point): point is { at: string | null; value: number } => point.value != null)
    .sort((left, right) => (left.at ?? '').localeCompare(right.at ?? ''));
  if (points.length < 2) return null;
  const da = danish(input.language);
  const max = Math.max(...points.map((point) => point.value), 1);
  return {
    id: 'history',
    kind: 'history',
    label: da ? 'Udvikling' : 'Trend',
    value: points[points.length - 1]?.value ?? 0,
    max,
    points: points.map((point) => ({
      label: formatClinicDateOnly(point.at, input.language) || (point.at ?? '').slice(0, 10) || '–',
      value: point.value,
    })),
  };
}

export function withHistoryChart(
  charts: readonly ChartFigure[],
  history: ChartFigure | null,
): ChartFigure[] {
  if (!history) return [...charts];
  if (charts.some((chart) => chart.id === 'history')) return [...charts];
  return [...charts, history];
}

export function questionnaireChartChecked(selection: ChartSelection, chartId: string): boolean {
  if (!selection.parts.charts) return false;
  if (selection.chartIds == null) return true;
  return selection.chartIds.includes(chartId);
}

/** Master switch. On keeps every figure. Off keeps none. */
export function setQuestionnaireCharts(enabled: boolean): Pick<ChartSelection, 'parts' | 'chartIds'> {
  return {
    parts: { charts: enabled },
    chartIds: null,
  };
}

/**
 * One figure's switch.
 * `parts` here is only the charts flag; the caller keeps the other flags.
 */
export function toggleQuestionnaireChart(
  selection: ChartSelection,
  chartId: string,
  enabled: boolean,
  availableIds: readonly string[],
): { charts: boolean; chartIds: string[] | null } {
  const current = selection.parts.charts ? (selection.chartIds ?? [...availableIds]) : [];
  const next = enabled
    ? current.includes(chartId)
      ? current
      : [...current, chartId]
    : current.filter((id) => id !== chartId);
  const known = availableIds.filter((id) => next.includes(id));
  if (known.length === 0) return { charts: false, chartIds: null };
  if (availableIds.length > 0 && availableIds.every((id) => known.includes(id))) {
    return { charts: true, chartIds: null };
  }
  return { charts: true, chartIds: known };
}

export function selectQuestionnaireCharts(
  charts: readonly ChartFigure[],
  selection: ChartSelection,
): ChartFigure[] {
  const visible = charts.filter((chart) => chart.label.trim().length > 0);
  if (!selection.parts.charts) return [];
  if (selection.chartIds == null) return visible;
  const allow = new Set(selection.chartIds);
  return visible.filter((chart) => chart.id != null && allow.has(chart.id));
}
